import { useEffect, useState } from 'react';

// Registers the service worker and tracks whether the browser will let us
// offer "Install app". Chrome and Edge (Android, Windows, macOS, ChromeOS,
// Linux) fire `beforeinstallprompt` once the app qualifies; we hold on to that
// event so a button can trigger the native install dialog later. Safari never
// fires it — on iPhone/iPad the user installs via Share → Add to Home Screen.

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function isIOS() {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports itself as Mac; the touch check tells them apart.
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

export function registerPWA() {
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault(); // keep Chrome's mini-infobar from popping up; we show our own button
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });

  // Only in production builds: a service worker in `npm start` would cache the
  // dev bundle and make hot reload confusing.
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`).catch(() => {});
    });
  }
}

export async function promptInstall() {
  if (!deferredPrompt) return false;
  const e = deferredPrompt;
  deferredPrompt = null;
  notify();
  e.prompt();
  const { outcome } = await e.userChoice;
  return outcome === 'accepted';
}

// 'prompt' — native install dialog is available (Chrome/Edge)
// 'ios'    — Safari on iPhone/iPad: show Add to Home Screen instructions
// null     — already installed, or the browser can't install it
function installState() {
  if (isStandalone()) return null;
  if (deferredPrompt) return 'prompt';
  if (isIOS()) return 'ios';
  return null;
}

export function useInstallState() {
  const [state, setState] = useState(installState);
  useEffect(() => {
    const update = () => setState(installState());
    listeners.add(update);
    update();
    return () => listeners.delete(update);
  }, []);
  return state;
}
